import type { GameModeConfig } from '../game/types';

export type RankedModeCode =
  | 'ranked_standard_solo_v1'
  | 'ranked_standard_duo_v1'
  | 'ranked_hardcore_solo_v1'
  | 'ranked_hardcore_duo_v1';

export type RankedVariant = 'standard' | 'hardcore';

export interface RankedModeDefinition {
  code: RankedModeCode;
  labelKey: string;
  subtitleKey: string;
  playerCount: 1 | 2;
  variant: RankedVariant;
  config: GameModeConfig;
}

const STANDARD_CONFIG: Omit<GameModeConfig, 'playerCount'> = {
  infiniteLives: false,
  invincibleBase: false,
  friendlyFire: false,
  upgradeDropRate: 'normal',
  loseUpgradeOnDeath: false,
  levelTransitionDelay: 5,
  obstacleDensity: 'high',
  initialPlayerLevel: 1,
  enemyAiMode: 'classic',
  playerTankClasses: ['standard', 'standard'],
};

const HARDCORE_CONFIG: Omit<GameModeConfig, 'playerCount'> = {
  infiniteLives: false,
  invincibleBase: false,
  friendlyFire: true,
  upgradeDropRate: 'low',
  loseUpgradeOnDeath: true,
  levelTransitionDelay: 5,
  obstacleDensity: 'dense',
  initialPlayerLevel: 1,
  enemyAiMode: 'enhanced',
  playerTankClasses: ['standard', 'standard'],
};

export const RANKED_MODES: Record<RankedModeCode, RankedModeDefinition> = {
  ranked_standard_solo_v1: {
    code: 'ranked_standard_solo_v1',
    labelKey: 'ranked.preset.standardSolo',
    subtitleKey: 'ranked.variant.standardHint',
    playerCount: 1,
    variant: 'standard',
    config: { ...STANDARD_CONFIG, playerCount: 1 },
  },
  ranked_standard_duo_v1: {
    code: 'ranked_standard_duo_v1',
    labelKey: 'ranked.preset.standardDuo',
    subtitleKey: 'ranked.variant.standardHint',
    playerCount: 2,
    variant: 'standard',
    config: { ...STANDARD_CONFIG, playerCount: 2 },
  },
  ranked_hardcore_solo_v1: {
    code: 'ranked_hardcore_solo_v1',
    labelKey: 'ranked.preset.hardcoreSolo',
    subtitleKey: 'ranked.variant.hardcoreHint',
    playerCount: 1,
    variant: 'hardcore',
    config: { ...HARDCORE_CONFIG, playerCount: 1 },
  },
  ranked_hardcore_duo_v1: {
    code: 'ranked_hardcore_duo_v1',
    labelKey: 'ranked.preset.hardcoreDuo',
    subtitleKey: 'ranked.variant.hardcoreHint',
    playerCount: 2,
    variant: 'hardcore',
    config: { ...HARDCORE_CONFIG, playerCount: 2 },
  },
};

export const DEFAULT_RANKED_MODE: RankedModeCode = 'ranked_standard_duo_v1';

export function rankedModeForConfig(mode: GameModeConfig): RankedModeCode | null {
  for (const [code, def] of Object.entries(RANKED_MODES) as Array<[RankedModeCode, RankedModeDefinition]>) {
    if (isExactRankedConfig(mode, def.config)) return code;
  }
  return null;
}

export function isExactRankedConfig(mode: GameModeConfig, expected: GameModeConfig): boolean {
  return mode.infiniteLives === expected.infiniteLives
    && mode.invincibleBase === expected.invincibleBase
    && mode.friendlyFire === expected.friendlyFire
    && mode.upgradeDropRate === expected.upgradeDropRate
    && mode.loseUpgradeOnDeath === expected.loseUpgradeOnDeath
    && mode.playerCount === expected.playerCount
    && mode.levelTransitionDelay === expected.levelTransitionDelay
    && mode.obstacleDensity === expected.obstacleDensity
    && mode.initialPlayerLevel === expected.initialPlayerLevel
    && mode.enemyAiMode === expected.enemyAiMode
    && (mode.playerTankClasses?.[0] ?? 'standard') === (expected.playerTankClasses?.[0] ?? 'standard')
    && (mode.playerTankClasses?.[1] ?? 'standard') === (expected.playerTankClasses?.[1] ?? 'standard');
}
